import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleHotDogGuideVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "10-best-hot-dog-spots-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1-japadog": "Japadog",
    "section-2-brathaus": "BratHaus",
    "section-3-labellepatate": "La Belle Patate",
    "section-4-meanpoutine": "Mean Poutine",
    "section-5-mrtubesteak": "Mr. Tube Steak Cart",
    "section-6-englishbay": "English Bay Hot Dog Stand",
    "section-7-dougiedog": "DougieDog Diner Truck",
    "section-8-costco": "Costco Food Court",
    "section-9-chungchun": "Chung Chun Rice Dog",
    "section-10-kaisereck": "Kaisereck Delicatessen",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/doing-business/food-vending.aspx",
      label: "City of Vancouver – Street Food Vendor Permits",
    },
    {
      url: "https://streetfoodapp.com/vancouver",
      label: "StreetFoodApp Vancouver – Live Food Truck Map",
    },
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Official Tourism",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1-japadog",
      lat: 49.2804,
      lng: -123.1186,
      popupText: "Japadog (530 Robson St)",
    },
    {
      id: "section-2-brathaus",
      lat: 49.2825,
      lng: -123.1080,
      popupText: "BratHaus (435 Abbott St)",
    },
    {
      id: "section-3-labellepatate",
      lat: 49.2818,
      lng: -123.1340,
      popupText: "La Belle Patate (1215 Davie St)",
    },
    {
      id: "section-4-meanpoutine",
      lat: 49.2790,
      lng: -123.1210,
      popupText: "Mean Poutine (718 Nelson St)",
    },
    {
      id: "section-5-mrtubesteak",
      lat: 49.2827,
      lng: -123.1207,
      popupText: "Mr. Tube Steak Cart (Granville & W. Georgia)",
    },
    {
      id: "section-6-englishbay",
      lat: 49.2877,
      lng: -123.1419,
      popupText: "English Bay Hot Dog Stand (1800 Morton Ave)",
    },
    {
      id: "section-7-dougiedog",
      lat: 49.2780,
      lng: -123.1250,
      popupText: "DougieDog (Food Truck, various locations)",
    },
    {
      id: "section-8-costco",
      lat: 49.2767,
      lng: -123.1124,
      popupText: "Costco Food Court (605 Expo Blvd)",
    },
    {
      id: "section-9-chungchun",
      lat: 49.2893,
      lng: -123.1350,
      popupText: "Chung Chun Rice Dog (1795 Robson St)",
    },
    {
      id: "section-10-kaisereck",
      lat: 49.2711,
      lng: -123.1335,
      popupText: "Kaisereck Delicatessen (Granville Island)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>10 Best Hot Dog Spots in Vancouver</title>
        <meta
          name="description"
          content="Discover the 10 best hot dog spots in Vancouver, ranging from classic street carts to gourmet fusion joints."
        />
        <meta
          name="keywords"
          content="hot dogs, Vancouver, street food, dining, foodie"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/10-best-hot-dog-spots-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="10 Best Hot Dog Spots in Vancouver"
        />
        <meta
          property="og:description"
          content="A thorough guide to the top places in Vancouver for mouth-watering hot dogs. Find details, addresses, tips, and more!"
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/10-best-hot-dog-spots-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/10-best-hot-dog-spots-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="10 Best Hot Dog Spots in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Check out the ultimate hot dog guide in Vancouver - from Japanese fusion to classic beachside carts!"
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/10-best-hot-dog-spots-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Vancouver Hot Dog"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s street food has transformed from simple hot dog carts
              to an eclectic mix of gourmet and fusion offerings. Whether you
              want a classic downtown smokie or a Japanese-style dog loaded with
              seaweed and teriyaki sauce, this city has you covered. In this
              guide, we’ll look at the 10 best hot dog spots across Vancouver—
              from beachside stands to late-night joints—so you can indulge in a
              tasty, budget-friendly bite.
            </p>

            {/* SECTION 1: Japadog */}
            <h2 id="section-1-japadog">1. Japadog</h2>
            <p
              ref={(el) => setSpotRef("section-1-japadog", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Address:</strong> 530 Robson St, Vancouver, BC V6B 2B7{" "}
              <Marker
                coordY={49.2804}
                coordX={-123.1186}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Operating Hours:</strong> Daily 11:00 AM – 10:00+ PM (varies
              by location)
            </p>
            <p>
              <strong>Price Range:</strong> Affordable ($6–$9)
            </p>
            <p>
              <strong>Why It’s Great:</strong> Japadog is a fusion sensation
              with unique toppings like teriyaki sauce, Japanese mayo, and
              seaweed on their Kurobuta pork or wagyu beef dogs. Locals and
              tourists alike flock here for the signature Terimayo dog. Multiple
              carts and a storefront mean you can almost always find a Japadog
              near you.
            </p>

            {/* SECTION 2: BratHaus */}
            <h2 id="section-2-brathaus">2. BratHaus</h2>
            <p
              ref={(el) => setSpotRef("section-2-brathaus", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Address:</strong> 435 Abbott St, Vancouver, BC V6B 2L2{" "}
              <Marker
                coordY={49.2825}
                coordX={-123.1080}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Operating Hours:</strong> Mon–Wed 12:00 PM – 10:00 PM;
              Thu–Sun 12:00 PM – 11:00 PM
            </p>
            <p>
              <strong>Price Range:</strong> Mid-range ($12–$15 for gourmet dogs)
            </p>
            <p>
              <strong>Why It’s Great:</strong> Located in the heart of Gastown,
              BratHaus brings high-quality German-style sausages to Vancouver.
              Their menu features creative offerings, like chorizo with
              chimichurri and a classic beer brat with sauerkraut. Dine-in
              options include local craft beers and indulgent sides.
            </p>

            {/* SECTION 3: La Belle Patate */}
            <h2 id="section-3-labellepatate">3. La Belle Patate</h2>
            <p
              ref={(el) => setSpotRef("section-3-labellepatate", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Address:</strong> 1215 Davie St, Vancouver, BC V6E 1N4{" "}
              <Marker
                coordY={49.2818}
                coordX={-123.1340}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Operating Hours:</strong> Tue–Thu 11:00 AM – 9:00 PM;
              Fri–Sat 11:00 AM – 11:00 PM; Sun 1:00 PM – 7:00 PM; Mon closed
            </p>
            <p>
              <strong>Price Range:</strong> Affordable ($5–$15)
            </p>
            <p>
              <strong>Why It’s Great:</strong> Craving a Montreal-style steamie?
              La Belle Patate offers authentic Quebecois hot dogs and famous
              poutine. Try a Michigan dog (topped with meat sauce) or load up
              your poutine with sliced hot dog for a deliciously messy meal.
            </p>

            {/* SECTION 4: Mean Poutine */}
            <h2 id="section-4-meanpoutine">4. Mean Poutine</h2>
            <p
              ref={(el) => setSpotRef("section-4-meanpoutine", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Address:</strong> 718 Nelson St, Vancouver, BC V6Z 1L2{" "}
              <Marker
                coordY={49.2790}
                coordX={-123.1210}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Operating Hours:</strong> Mon–Thu 11:00 AM – 11:00 PM;
              Fri–Sat 11:00 AM – 4:00 AM; Sun closed
            </p>
            <p>
              <strong>Price Range:</strong> Affordable ($6–$8 for hot dogs)
            </p>
            <p>
              <strong>Why It’s Great:</strong> Located near Granville’s
              nightlife, Mean Poutine is a favorite late-night spot. Enjoy
              everything from chili cheese to mac & cheese hot dogs. Vegetarian
              dog options and poutine with meat-free gravy are also available.
            </p>

            {/* SECTION 5: Mr. Tube Steak Cart */}
            <h2 id="section-5-mrtubesteak">5. Mr. Tube Steak Cart</h2>
            <p
              ref={(el) => setSpotRef("section-5-mrtubesteak", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> Corner of Granville St &amp; W. Georgia
              St{" "}
              <Marker
                coordY={49.2827}
                coordX={-123.1207}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Operating Hours:</strong> Typically 11:00 AM until evening
              (varies)
            </p>
            <p>
              <strong>Price Range:</strong> Affordable ($4–$7)
            </p>
            <p>
              <strong>Why It’s Great:</strong> An iconic Vancouver street cart,
              Mr. Tube Steak serves classic beef smokies with fried onions,
              sauerkraut, and garlic sauce. This no-frills option is beloved by
              locals for a quick, tasty bite.
            </p>

            {/* SECTION 6: English Bay Hot Dog Stand */}
            <h2 id="section-6-englishbay">6. English Bay Hot Dog Stand</h2>
            <p
              ref={(el) => setSpotRef("section-6-englishbay", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Address:</strong> 1800 Morton Ave (near English Bay Beach){" "}
              <Marker
                coordY={49.2877}
                coordX={-123.1419}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Operating Hours:</strong> Seasonal; generally late morning
              to sunset
            </p>
            <p>
              <strong>Price Range:</strong> Affordable (~$5)
            </p>
            <p>
              <strong>Why It’s Great:</strong> Perfect for a beach day. Grab a
              smokie topped with fried onions and a famous creamy garlic sauce
              while enjoying beautiful ocean views. Popular with locals and
              tourists alike, it’s a classic summer experience.
            </p>

            {/* SECTION 7: DougieDog Diner Truck */}
            <h2 id="section-7-dougiedog">7. DougieDog Diner Truck</h2>
            <p
              ref={(el) => setSpotRef("section-7-dougiedog", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> Roaming food truck (base near 49.2780,
              -123.1250){" "}
              <Marker
                coordY={49.2780}
                coordX={-123.1250}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Operating Hours:</strong> Check social media for current
              schedule
            </p>
            <p>
              <strong>Price Range:</strong> Mid-range ($8–$12, $100 for “Dragon
              Dog”)
            </p>
            <p>
              <strong>Why It’s Great:</strong> DougieDog made waves with its
              $100 “Dragon Dog” infused with cognac, topped with lobster and
              truffles. Their regular gourmet hot dogs are more wallet-friendly,
              featuring bacon mac & cheese dogs, baked beans, and house-made
              root beer. Follow them online to find their current location.
            </p>

            {/* SECTION 8: Costco Food Court */}
            <h2 id="section-8-costco">8. Costco Food Court</h2>
            <p
              ref={(el) => setSpotRef("section-8-costco", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Address:</strong> 605 Expo Blvd, Vancouver, BC V6B 1W8{" "}
              <Marker
                coordY={49.2767}
                coordX={-123.1124}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Operating Hours:</strong> Follows Costco warehouse hours
            </p>
            <p>
              <strong>Price Range:</strong> Ultra affordable ($1.50 combo)
            </p>
            <p>
              <strong>Why It’s Great:</strong> If you’re looking for a cheap
              eat, Costco’s legendary $1.50 hot dog + soda combo can’t be beat.
              It’s a quarter-pound, all-beef hot dog, reliably tasty and
              shockingly inexpensive. A staple for budget-conscious locals.
            </p>

            {/* SECTION 9: Chung Chun Rice Dog */}
            <h2 id="section-9-chungchun">9. Chung Chun Rice Dog</h2>
            <p
              ref={(el) => setSpotRef("section-9-chungchun", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Address:</strong> 1795 Robson St, Vancouver, BC V6G 1C9{" "}
              <Marker
                coordY={49.2893}
                coordX={-123.1350}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Operating Hours:</strong> Daily ~11:00 AM – 10:00 PM
            </p>
            <p>
              <strong>Price Range:</strong> Affordable ($5–$7)
            </p>
            <p>
              <strong>Why It’s Great:</strong> If you’ve never tried a
              Korean-style corn dog, this is the place to start. Skewered dogs
              (or mozzarella cheese) are coated in a crispy rice flour batter,
              then topped with sugar, potato chunks, or squid ink batter. They’re
              Instagram-worthy and deliciously addictive.
            </p>

            {/* SECTION 10: Kaisereck Delicatessen */}
            <h2 id="section-10-kaisereck">10. Kaisereck Delicatessen</h2>
            <p
              ref={(el) => setSpotRef("section-10-kaisereck", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Address:</strong> 1689 Johnston St, Granville Island Public
              Market, Stall 114{" "}
              <Marker
                coordY={49.2711}
                coordX={-123.1335}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Operating Hours:</strong> Public Market hours (9:00 AM –
              6:00 PM, daily)
            </p>
            <p>
              <strong>Price Range:</strong> Mid-range ($8–$10)
            </p>
            <p>
              <strong>Why It’s Great:</strong> Offering an assortment of European
              sausages inside the bustling Granville Island Public Market,
              Kaisereck serves everything from bratwurst to turkey smokies. Load
              up with your choice of condiments at their topping bar and enjoy
              the island’s waterfront views.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Vancouver’s mobile vendors and street carts operate under strict
              guidelines set by the city and Vancouver Coastal Health. All
              vendors must have a proper business license, undergo food safety
              inspections, and display their permit. Food trucks need separate
              permits to park in designated areas, and they must renew these
              permits annually. As a consumer, look for the permit decal or ask
              if you’re unsure—licensed vendors are typically very forthcoming
              about their certifications.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Are vegan or vegetarian options available?</h3>
              <p>
                Yes! Most popular hot dog spots offer veggie alternatives, such
                as a vegetarian smokie or entirely plant-based options. Japadog,
                Mean Poutine, and others provide veggie dogs upon request.
              </p>

              <h3>2. How much do hot dogs typically cost in Vancouver?</h3>
              <p>
                Prices range from around $4 for a basic street cart hot dog up to
                $15 for gourmet options. Costco has the cheapest deal at just
                $1.50 with a drink. Most are in the $6–$9 range for a fully
                loaded dog.
              </p>

              <h3>3. Are late-night hot dog spots common in Vancouver?</h3>
              <p>
                Absolutely. Many street vendors stay open until late, and spots
                like Mean Poutine are open until 4:00 AM on weekends, catering to
                the nightlife crowd.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s hot dog scene truly has something for everyone—from
              budget bites at Costco to lavish $100 indulgences at DougieDog.
              Whether it’s a Japanese twist from Japadog or a classic beachside
              treat at English Bay, each spot offers a unique flavor of the city.
              So go ahead and explore: follow the savory aroma, grab a local
              craft beer to pair, and enjoy the best “haute dogs” Vancouver has
              to offer.
            </p>

            {/* Disclaimer */}
            <p>
              <em>
                Disclaimer: Always double-check vendor hours and locations, as
                food trucks can move around or change schedules. For specific
                dietary needs, contact vendors directly. Follow local guidelines
                for safe consumption.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}